import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Attendance from './pages/Attendance';
import Fees from './pages/Fees';
import Exams from './pages/Exams';
import Certificates from './pages/Certificates';
import Placement from './pages/Placement';
import Hostel from './pages/Hostel';
import MasterData from './pages/MasterData';
import Assignments from './pages/Assignments';
import FeeSchedules from './pages/FeeSchedules';
import LanguageTraining from './pages/LanguageTraining';
import Requests from './pages/Requests';
import StaffAdmin from './pages/StaffAdmin';
import Applications from './pages/Applications';
import Apply from './pages/Apply';
import ActivityLog from './pages/ActivityLog';
import HR from './pages/HR';
import Internship from './pages/Internship';
import NACTET from './pages/NACTET';
import Accounts from './pages/Accounts';
import GST from './pages/GST';
import CivilConstruction from './pages/CivilConstruction';
import MarketingCRM from './pages/MarketingCRM';
import Concerns from './pages/Concerns';
import StaffExpenses from './pages/StaffExpenses';import Assets from './pages/Assets';


class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error: error.message }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{padding:30,textAlign:'center',fontFamily:'sans-serif',background:'#fef2f2',minHeight:'100vh',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center'}}>
          <div style={{fontSize:48,marginBottom:16}}>⚠️</div>
          <h2 style={{color:'#dc2626',marginBottom:12}}>Something went wrong</h2>
          <p style={{color:'#991b1b',background:'#fee2e2',padding:'12px 20px',borderRadius:8,maxWidth:500,fontSize:13,wordBreak:'break-word'}}>
            {this.state.error}
          </p>
          <button onClick={()=>window.location.reload()} style={{marginTop:20,padding:'10px 24px',background:'#dc2626',color:'#fff',border:'none',borderRadius:8,cursor:'pointer',fontSize:14}}>
            Reload App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="loading" style={{ height: '100vh' }}>
      <div className="spinner" />
      <span style={{ color: 'var(--text3)', fontSize: 14 }}>Loading...</span>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Menu items are hidden by Layout, but a hidden page could still be opened by typing its address.
// Every page is therefore checked against the same permission key the menu uses.
function RequireView({ pkey, children }) {
  const { canView, loading } = useAuth();
  if (loading) return (
    <div className="loading" style={{ height: '60vh' }}><div className="spinner" /></div>
  );
  if (pkey && typeof canView === 'function' && !canView(pkey)) return (
    <div className="page">
      <div className="empty-state">
        <div style={{ fontSize: 40, marginBottom: 8 }}>🚫</div>
        <h3>You don't have access to this section</h3>
        <p>Ask the administrator if you need it.</p>
      </div>
    </div>
  );
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
        <Route path="/apply" element={<Apply />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<RequireView pkey="dashboard"><Dashboard /></RequireView>} />
        <Route path="students"    element={<RequireView pkey="students"><Students /></RequireView>} />
        <Route path="attendance"  element={<RequireView pkey="attendance"><Attendance /></RequireView>} />
        <Route path="fees"        element={<RequireView pkey="fees"><Fees /></RequireView>} />
        <Route path="exams"       element={<RequireView pkey="exams"><Exams /></RequireView>} />
        <Route path="certificates" element={<RequireView pkey="certificates"><Certificates /></RequireView>} />
        <Route path="placement"   element={<RequireView pkey="placement"><Placement /></RequireView>} />
        <Route path="hostel"      element={<RequireView pkey="hostel"><Hostel /></RequireView>} />
        <Route path="master"      element={<RequireView pkey="master_data"><MasterData /></RequireView>} />
        <Route path="assignments" element={<RequireView pkey="assignments"><Assignments /></RequireView>} />
        <Route path="fee-schedules" element={<RequireView pkey="fee_schedules"><FeeSchedules /></RequireView>} />
        <Route path="language"    element={<RequireView pkey="language_training"><LanguageTraining /></RequireView>} />
        <Route path="requests"    element={<RequireView pkey="requests"><Requests /></RequireView>} />
        <Route path="applications" element={<RequireView pkey="applications"><Applications /></RequireView>} />
            <Route path="staff"       element={<RequireView pkey="staff_admin"><StaffAdmin /></RequireView>} />
        <Route path="activity"    element={<RequireView pkey="staff_admin"><ActivityLog /></RequireView>} />
        <Route path="hr"          element={<RequireView pkey="hr"><HR /></RequireView>} />
        <Route path="internship"  element={<RequireView pkey="internship"><Internship /></RequireView>} />
        <Route path="nactet"      element={<RequireView pkey="nactet"><NACTET /></RequireView>} />
        <Route path="accounts"    element={<RequireView pkey="accounts"><Accounts /></RequireView>} />
        <Route path="gst"         element={<RequireView pkey="gst"><GST /></RequireView>} />
        <Route path="civil"       element={<RequireView pkey="civil"><CivilConstruction /></RequireView>} />
        <Route path="crm"         element={<RequireView pkey="crm"><MarketingCRM /></RequireView>} />
        <Route path="concerns"     element={<RequireView pkey="concerns"><Concerns /></RequireView>} />
        <Route path="expenses"    element={<RequireView pkey="expenses"><StaffExpenses /></RequireView>} />
<Route path="assets" element={<RequireView pkey="assets"><Assets /></RequireView>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            style: { background: 'var(--surface2)', color: 'var(--text)', border: '1px solid var(--border)', fontSize: 13 },
            success: { iconTheme: { primary: 'var(--green)', secondary: 'var(--bg2)' } },
            error:   { iconTheme: { primary: 'var(--red)',   secondary: 'var(--bg2)' } },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}
